import React, { useState, useEffect } from 'react';
import '../../styles/admin/AdminDashboard.css';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    projects: 0,
    news: 0,
    totalViews: 0,
    activeProjects: 0
  });
  const [loading, setLoading] = useState(true);
  const [recentProjects, setRecentProjects] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);

  const API_BASE_URL = "https://phucansolutions.vn" || '"https://phucansolutions.vn"';

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      // Fetch dashboard statistics
      const [statsRes, projectsRes, activitiesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/admin/dashboard/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/api/admin/dashboard/recent-projects?limit=5`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/api/admin/dashboard/activities?limit=10`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        if (statsData.success) {
          setStats({
            projects: statsData.data.totalProjects || 0,
            news: 0, // Will fetch separately if needed
            totalViews: 12543, // Demo data
            activeProjects: statsData.data.inProgressProjects || 0
          });
        }
      }

      if (projectsRes.ok) {
        const projectsData = await projectsRes.json();
        if (projectsData.success) {
          setRecentProjects(projectsData.data || []);
        }
      }

      if (activitiesRes.ok) {
        const activitiesData = await activitiesRes.json();
        if (activitiesData.success) {
          setRecentActivities(activitiesData.data || []);
        }
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Tổng Dự Án',
      value: stats.projects,
      icon: '🚀',
      color: 'blue',
      trend: '+12%'
    },
    {
      title: 'Tin Tức',
      value: stats.news,
      icon: '📰',
      color: 'green',
      trend: '+8%'
    },
    {
      title: 'Lượt Xem',
      value: stats.totalViews.toLocaleString(),
      icon: '👁️',
      color: 'purple',
      trend: '+25%'
    },
    {
      title: 'Đang Thực Hiện',
      value: stats.activeProjects,
      icon: '⚡',
      color: 'orange',
      trend: '+3'
    }
  ];

  const getStatusText = (status) => {
    const statusMap = {
      'planning': 'Lên kế hoạch',
      'in-progress': 'Đang thực hiện',
      'completed': 'Hoàn thành',
      'on-hold': 'Tạm dừng'
    };
    return statusMap[status] || status;
  };

  const getPriorityText = (priority) => {
    const priorityMap = {
      'low': 'Thấp',
      'medium': 'Trung bình',
      'high': 'Cao',
      'urgent': 'Khẩn cấp'
    };
    return priorityMap[priority] || priority;
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner">🔄</div>
        <p>Đang tải dashboard...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <p>Tổng quan hệ thống PhucAn Solutions</p>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        {statCards.map((card, index) => (
          <div key={index} className={`stat-card ${card.color}`}>
            <div className="stat-icon">{card.icon}</div>
            <div className="stat-content">
              <h3>{card.value}</h3>
              <p>{card.title}</p>
              <span className="stat-trend">{card.trend}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="dashboard-content">
        {/* Recent Projects */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>🚀 Dự Án Gần Đây</h2>
            <a href="/admin/projects" className="view-all-btn">Xem tất cả →</a>
          </div>
          <div className="recent-items">
            {recentProjects.length > 0 ? (
              recentProjects.map((project) => (
                <div key={project.id} className="recent-item">
                  <div className="item-info">
                    <h4>{project.title}</h4>
                    <p>{project.description?.substring(0, 100) || 'Không có mô tả'}...</p>
                    <div className="item-meta">
                      <span className={`status ${project.status}`}>
                        {getStatusText(project.status)}
                      </span>
                      {project.priority && (
                        <span className="category">{getPriorityText(project.priority)}</span>
                      )}
                      {project.progress && (
                        <span className="category">{project.progress}%</span>
                      )}
                    </div>
                  </div>
                  <div className="item-actions">
                    <button className="edit-btn" title="Chỉnh sửa">✏️</button>
                    <button className="view-btn" title="Xem chi tiết">👁️</button>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <p>Chưa có dự án nào</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Activities */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>📋 Hoạt Động Gần Đây</h2>
            <a href="/admin/activities" className="view-all-btn">Xem tất cả →</a>
          </div>
          <div className="recent-items">
            {recentActivities.length > 0 ? (
              recentActivities.map((activity, index) => (
                <div key={index} className="recent-item">
                  <div className="item-info">
                    <h4>{activity.action}</h4>
                    <p>{activity.details || 'Không có chi tiết'}</p>
                    <div className="item-meta">
                      <span className="date">
                        {new Date(activity.timestamp).toLocaleDateString('vi-VN')}
                      </span>
                      {activity.user_name && (
                        <span className="category">{activity.user_name}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <p>Chưa có hoạt động nào</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h2>Thao Tác Nhanh</h2>
        <div className="actions-grid">
          <a href="/admin/projects/create" className="action-card">
            <div className="action-icon">➕</div>
            <div className="action-text">
              <h3>Tạo Dự Án</h3>
              <p>Thêm dự án mới</p>
            </div>
          </a>
          <a href="/admin/news/create" className="action-card">
            <div className="action-icon">📝</div>
            <div className="action-text">
              <h3>Viết Tin Tức</h3>
              <p>Tạo bài viết mới</p>
            </div>
          </a>
          <a href="/admin/uploads" className="action-card">
            <div className="action-icon">📎</div>
            <div className="action-text">
              <h3>Upload File</h3>
              <p>Quản lý file media</p>
            </div>
          </a>
          <a href="/admin/users" className="action-card">
            <div className="action-icon">👥</div>
            <div className="action-text">
              <h3>Quản Lý User</h3>
              <p>Thêm/sửa người dùng</p>
            </div>
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 